import React from 'react';
import {Group} from '@visx/group';
import {AreaClosed} from '@visx/shape';
import {AxisBottom, AxisLeft, AxisScale} from '@visx/axis';
import {LinearGradient} from '@visx/gradient';
import {curveMonotoneX} from '@visx/curve';
import {CallStatisticsMetricsItem} from './CallStatistics.utils';
import {GridRows} from '@visx/grid';
import dayjs from '../../../services/customDayJs';

const axisColor = 'rgba(146, 146, 157, 1)';
const axisBottomTickLabelProps = {
  textAnchor: 'end' as const,
  fontSize: 12,
  fill: axisColor,
  dy: '-0.5em',
  fontFamily: 'Roboto'
};

const axisLeftTickLabelProps = {
  dx: '-0.25em',
  dy: '0.25em',
  fontFamily: 'Arial',
  fontSize: 10,
  textAnchor: 'end' as const,
  fill: axisColor,
};

const getDate = (d: CallStatisticsMetricsItem) => d.date;

export default function AreaChart({
  data,
  gradientColor,
  width,
  yMax,
  margin,
  xScale,
  yScale,
  hideBottomAxis = false,
  hideLeftAxis = false,
  top,
  left,
  children,
  outgoingShown,
  incomingShown
}: {
  data: CallStatisticsMetricsItem[];
  gradientColor: string;
  xScale: AxisScale<number>;
  yScale: AxisScale<number>;
  width: number;
  yMax: number;
  margin: { top: number; right: number; bottom: number; left: number };
  hideBottomAxis?: boolean;
  hideLeftAxis?: boolean;
  top?: number;
  left?: number;
  children?: React.ReactNode;
  outgoingShown: boolean;
  incomingShown: boolean;
}) {
  if (width < 10) return null;

  const formatDate = (val: Date | string | null) => {
    return dayjs(val).format('MMM DD');
  };

  return (
    <Group left={left || margin.left} top={top || margin.top}>
      <GridRows
        scale={yScale}
        width={width - margin.left - margin.right}
        top={0}
        left={0}
        stroke="rgba(221, 225, 231, 1)"
        pointerEvents="none"
        numTicks={3}
      />
      <LinearGradient
        id="gradient"
        from={gradientColor}
        fromOpacity={1}
        to={gradientColor}
        toOpacity={0.2}
      />
      {outgoingShown && (
        <AreaClosed<CallStatisticsMetricsItem>
          data={data}
          x={(d) => xScale(getDate(d)) || 0}
          y={(d) => yScale(d.outgoing) || 0}
          yScale={yScale}
          stroke={"rgba(57, 105, 229, 1)"}
          fill={"rgba(57, 105, 229, 0.2)"}
          strokeWidth={1}
          curve={curveMonotoneX}
        />
      )}
      {incomingShown && (
        <AreaClosed<CallStatisticsMetricsItem>
          data={data}
          x={(d) => xScale(getDate(d)) || 0}
          y={(d) => yScale(d.incoming) || 0}
          yScale={yScale}
          stroke={"rgba(245, 132, 31, 1)"}
          fill={"rgba(245, 132, 31, 0.2)"}
          strokeWidth={1}
          curve={curveMonotoneX}
        />
      )}
      {!hideBottomAxis && (
        <AxisBottom
          top={yMax}
          scale={xScale}
          numTicks={(width > 520) ? 10 : 5}
          stroke={axisColor}
          tickStroke={axisColor}
          tickLabelProps={axisBottomTickLabelProps}
          hideTicks
          tickFormat={formatDate}
        />
      )}
      {!hideLeftAxis && (
        <AxisLeft
          scale={yScale}
          numTicks={5}
          stroke={axisColor}
          tickStroke={axisColor}
          tickLabelProps={axisLeftTickLabelProps}
        />
      )}
      {children}
    </Group>
  );
}
